import React, { useState, useRef, useEffect } from 'react';
import { BaseEdge, EdgeLabelRenderer, getSmoothStepPath } from '@xyflow/react';
import { Delete16Regular } from '@fluentui/react-icons';
import './CSS/CustomEdge.css';

/**
 * Custom Edge pour ChatFlow avec bouton de suppression et label éditable
 */
export default function CustomEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  label,
  data,
}) {
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(label || '');
  const inputRef = useRef(null);

  // Synchroniser editValue avec le label qui vient des props
  useEffect(() => {
    if (!isEditing) {
      setEditValue(label || '');
    }
  }, [label, isEditing]);

  // Focus l'input quand on passe en mode édition
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleDoubleClick = (e) => {
    e.stopPropagation();
    // Seulement éditable si c'est un edge avec onLabelChange
    if (data?.onLabelChange) {
      setIsEditing(true);
      setEditValue(label || '');
    }
  };

  const handleSave = () => {
    const trimmedValue = editValue.trim();
    
    
    // Si vide, garder l'ancien label
    if (!trimmedValue) {
      setEditValue(label || '');
      setIsEditing(false);
      return;
    }
    
    // Si la valeur n'a pas changé, juste fermer l'input
    if (trimmedValue === label) {
      setIsEditing(false);
      return;
    }
    
    // Sauvegarder la nouvelle valeur
    if (data?.onLabelChange) {
      data.onLabelChange(id, trimmedValue);
    }
    
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setEditValue(label || '');
    }
  };

  const handleBlur = () => {
    handleSave();
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (data?.onDelete) {
      data.onDelete(id);
    }
  };

  return (
    <>
      <BaseEdge path={edgePath} markerEnd={markerEnd} style={style} />
      <EdgeLabelRenderer>
        <div
          className="custom-edge-label-wrapper"
          style={{
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
          }}
        >
          {isEditing ? (
            <input
              ref={inputRef}
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              className="editable-edge-input"
              placeholder="Action name"
              maxLength={50}
            />
          ) : (
            <>
              {/* Label de l'edge - double-cliquez pour éditer */}
              {label && (
                <div
                  className="custom-edge-label"
                  onDoubleClick={handleDoubleClick}
                  style={{
                    cursor: data?.onLabelChange ? 'pointer' : 'default',
                  }}
                  title={data?.onLabelChange ? 'Double-cliquez pour éditer' : ''}
                >
                  {label}
                </div>
              )}
              {/* Bouton de suppression */}
              {data?.onDelete && (
                <button
                  className="custom-edge-delete-btn"
                  onClick={handleDelete}
                  title="Supprimer la liaison"
                >
                  <Delete16Regular />
                </button>
              )}
            </>
          )}
        </div>
      </EdgeLabelRenderer>
    </>
  );
}
